import React, { useState, useEffect } from 'react';
import { useToast } from '../context/ToastContext';
import { Save } from 'lucide-react';
import api from '../services/api';

const AdminSettings = () => {
    const [settings, setSettings] = useState({
        smtp_host: '',
        smtp_port: '',
        smtp_user: '',
        smtp_pass: '',
        smtp_secure: 'false',
        admin_email_accidentes: '',
        telegram_welcome_message: '',
        telegram_greeting_message: ''
    });
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            // We need to implement this endpoint in backend or generic settings endpoint
            // For now assuming we will create a settings route
            const response = await api.get('/settings');

            const newSettings = { ...settings };
            if (Array.isArray(response)) {
                response.forEach(s => {
                    newSettings[s.setting_key] = s.setting_value;
                });
            }

            setSettings(newSettings);
        } catch (error) {
            console.error('Error loading settings:', error);
            toast.error('Error al cargar la configuración');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post('/settings', { settings });
            toast.success('Configuración guardada correctamente');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Error al guardar la configuración');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-white">Cargando configuración...</div>;

    return (
        <div className="max-w-4xl mx-auto p-6">
            <h1 className="text-2xl font-bold text-white mb-6">Configuración del Sistema</h1>

            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Email Notificaciones */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <h2 className="text-xl font-semibold text-blue-400 mb-4">Notificaciones de Accidentes</h2>
                    <div className="grid gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Email Destino</label>
                            <input
                                type="email"
                                name="admin_email_accidentes"
                                value={settings.admin_email_accidentes || ''}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="admin@tuvisa.es"
                            />
                            <p className="text-xs text-gray-400 mt-1">Este correo recibirá todos los partes de accidente generados.</p>
                        </div>
                    </div>
                </div>

                {/* Telegram Bot */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <h2 className="text-xl font-semibold text-[#0088cc] mb-4">Configuración de Telegram</h2>
                    <div className="grid gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Mensaje de Bienvenida (Saludo Inicial)</label>
                            <textarea
                                name="telegram_greeting_message"
                                value={settings.telegram_greeting_message || ''}
                                onChange={handleChange}
                                rows="4"
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-[#0088cc]"
                                placeholder="¡Hola! Bienvenido..."
                            />
                            <p className="text-xs text-gray-400 mt-2">
                                Este es el primer mensaje que recibe el usuario al usar /start o /registro.
                            </p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Mensaje de Aprobación (Envío de Ticket)</label>
                            <textarea
                                name="telegram_welcome_message"
                                value={settings.telegram_welcome_message || ''}
                                onChange={handleChange}
                                rows="6"
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-[#0088cc]"
                                placeholder="Escribe el mensaje de aprobación..."
                            />
                            <p className="text-xs text-gray-400 mt-2">
                                Usa la etiqueta <strong>{'{CODE}'}</strong> para que el bot inserte el número de Ticket. Soporta formato Markdown de Telegram.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Configuración SMTP */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <h2 className="text-xl font-semibold text-purple-400 mb-4">Configuración SMTP (Envío de Correos)</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Servidor SMTP (Host)</label>
                            <input
                                type="text"
                                name="smtp_host"
                                value={settings.smtp_host}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="smtp.office365.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Puerto</label>
                            <input
                                type="text"
                                name="smtp_port"
                                value={settings.smtp_port}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="587"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Seguridad (SSL/TLS)</label>
                            <select
                                name="smtp_secure"
                                value={settings.smtp_secure}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="false">STARTTLS (Puerto 587)</option>
                                <option value="true">SSL (Puerto 465)</option>
                            </select>
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Usuario SMTP</label>
                            <input
                                type="text"
                                name="smtp_user"
                                value={settings.smtp_user}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="usuario@dominio.com"
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Contraseña SMTP</label>
                            <input
                                type="password"
                                name="smtp_pass"
                                value={settings.smtp_pass}
                                onChange={handleChange}
                                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="********"
                            />
                        </div>

                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl font-bold text-white transition-colors disabled:opacity-50"
                    >
                        <Save className="w-5 h-5" />
                        {saving ? 'Guardando...' : 'Guardar Configuración'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default AdminSettings;
